import { Box, IconButton, Collapse, Typography } from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import DownloadIcon from '@mui/icons-material/Download'
import type { ReactNode, MouseEvent } from 'react'
import { FileIcon, FileName } from './index'
import type { FileType } from './FileIcon'

interface ChatFilePreviewProps {
  fileType: FileType
  fileName: string
  fileSize?: string
  isRefreshing: boolean
  expanded: boolean
  onToggle: (e: MouseEvent) => void
  onOpenInNew: (e: MouseEvent) => void
  preview: ReactNode
}

export const ChatFilePreview = ({
  fileType,
  fileName,
  fileSize,
  isRefreshing,
  expanded,
  onToggle,
  onOpenInNew,
  preview,
}: ChatFilePreviewProps) => {
  if (fileType === 'image') {
    return (
      <Box
        sx={{
          bgcolor: 'background.default',
          borderRadius: 1,
          px: 1.5,
          py: 0.5,
          fontSize: 14,
          boxShadow: 0,
          maxWidth: 400,
          mb: 1,
          opacity: isRefreshing ? 0.7 : 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <FileIcon fileType={fileType} size="small" />
          <FileName name={fileName} variant="chat" maxWidth={120} />
          <IconButton size="small" sx={{ ml: 0.5 }} onClick={onOpenInNew}>
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Box>
        {preview}
      </Box>
    )
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: 'fit-content',
          bgcolor: 'background.default',
          px: 0.5,
          py: 1,
          fontSize: 14,
          cursor: 'pointer',
          boxShadow: 0,
          '&:hover': { bgcolor: 'action.hover' },
          maxWidth: '100%',
          opacity: isRefreshing ? 0.7 : 1,
        }}
        onClick={onToggle}
      >
        <FileName name={fileName} variant="chat" maxWidth={240} />
        {fileSize && (
          <Typography component="span" sx={{ px: 0.5, fontSize: 13, color: 'text.secondary' }}>
            · {fileSize}
          </Typography>
        )}
        <IconButton size="small" sx={{ ml: 0.5 }} onClick={onOpenInNew} aria-label="Download">
          <DownloadIcon fontSize="small" />
        </IconButton>
      </Box>
      {fileType !== 'pdf' && (
        <Collapse in={expanded} unmountOnExit>
          <Box
            sx={{
              mt: 1,
              mb: 1,
              p: 1,
              bgcolor: 'background.paper',
              borderRadius: 1,
              boxShadow: 1,
              maxWidth: 600,
            }}
          >
            {preview}
          </Box>
        </Collapse>
      )}
    </>
  )
}
